/**
 * Getting nabu onto a home screen in as few taps as the platform allows (F-121).
 *
 * Three different offers, because there are three different truths:
 *
 * - **Chromium**: a real button that opens the browser's own install dialog. One tap.
 * - **iOS in Safari**: two taps, named exactly, with the Share glyph drawn rather than
 *   described — "tap Share" is useless to somebody who does not know which of the icons that
 *   is, and the icon is the whole of the step.
 * - **iOS in anything else**: a button that hands off to Safari at this same page, because the
 *   first step of the old instruction was "go to Safari" and it left the reader to do that
 *   themselves. That was most of Leo's complaint: *"if you're not on safari go to safari (I for
 *   one never use safari) then hit the three dots then share then more then add"*.
 *
 * What this deliberately does not do is show a button that looks like a one-tap install and
 * then explains itself. On iOS there is no install API at all, and a control that appears to
 * act and then produces a paragraph is worse than the paragraph alone: it spends the learner's
 * trust in every other button on the screen.
 */
import { useCallback, useEffect, useState } from 'react';
import { useI18n } from '../i18n';
import { installRoute, offersInstall, safariHandoff } from './route';
import { installFacts, onInstallOfferChange, showInstallPrompt } from './prompt';

/**
 * How long to wait before deciding the hand-off did nothing.
 *
 * Navigating to `x-safari-https://` either switches apps or is silently ignored; there is no
 * event for either. So we wait, and if the page is still here and visible we show the manual
 * path. Long enough that a successful hand-off has taken the page away first — the app switch
 * is fast but not instant — and short enough that somebody who is still looking at a stationary
 * screen has not yet decided the button is broken.
 */
const HANDOFF_MS = 1200;

/** The iOS share icon, drawn because naming it is the step people get stuck on. */
function ShareGlyph() {
  return (
    <svg className="inst-glyph" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        d="M12 3l3.5 3.5M12 3L8.5 6.5M12 3v12"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M6 11H5a1 1 0 00-1 1v8a1 1 0 001 1h14a1 1 0 001-1v-8a1 1 0 00-1-1h-1"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** The plus-in-a-square iOS shows beside "Add to Home Screen". */
function AddGlyph() {
  return (
    <svg className="inst-glyph" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <rect x="3.5" y="3.5" width="17" height="17" rx="4" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <path d="M12 8v8M8 12h8" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

export default function Install({ compact = false }: { compact?: boolean }) {
  const { t } = useI18n();
  /*
   * Re-read on every change rather than held as state, because two of the four facts move
   * underneath us: the offer arrives asynchronously when Chromium decides the site qualifies,
   * and `standalone` changes the moment the app is opened from the home screen.
   */
  const [, bump] = useState(0);
  useEffect(() => onInstallOfferChange(() => bump((n) => n + 1)), []);

  const [handingOff, setHandingOff] = useState(false);
  const [copied, setCopied] = useState(false);

  const route = installRoute(installFacts());

  const install = useCallback(async () => {
    const accepted = await showInstallPrompt();
    // Nothing to say either way. Accepted, the app appears and this component re-renders into
    // `installed`; dismissed, the browser has already told them what it thinks and repeating
    // it here would be nagging.
    void accepted;
  }, []);

  const toSafari = useCallback(() => {
    const target = safariHandoff(window.location.href);
    if (!target) return;
    /*
     * Deliberately *not* set here. Showing the manual path the instant the button is tapped
     * puts the wall of text back on screen for everybody, including the learner for whom the
     * hand-off is about to work — which is the entire failure this replaces. Only the timeout
     * below may set it, and only if we are still here to see it.
     */
    window.location.href = target;
    /*
     * If the scheme was ignored there is no error and no event, so the only signal is that we
     * are still here. `document.hidden` distinguishes the two: a successful hand-off has put
     * this page in the background by now.
     */
    window.setTimeout(() => {
      if (!document.hidden) setHandingOff(true);
    }, HANDOFF_MS);
  }, []);

  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(window.location.origin);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access is refused in plenty of ordinary situations and the address is on
      // screen anyway; a failure here needs no explanation.
    }
  }, []);

  if (!offersInstall(route)) return null;

  return (
    <section className={compact ? 'inst inst-compact' : 'inst'}>
      <h3 className="inst-title">{t('install.title')}</h3>
      {/*
        The reason differs by platform because the truth does. Only iOS requires an install
        before a learner can be notified at all; on Android and the desktop push works in an
        ordinary tab, so there the install is a convenience — and saying "the only way" to
        somebody it is not true for is a reason to press a button that they will later find out
        was invented.
      */}
      <p className="inst-why">
        {route === 'ios-safari' || route === 'ios-other' ? t('install.why.ios') : t('install.why')}
      </p>

      {route === 'prompt' && (
        <button type="button" className="inst-do" onClick={() => void install()}>
          {t('install.add')}
        </button>
      )}

      {route === 'ios-safari' && (
        <ol className="inst-steps">
          <li>
            <ShareGlyph />
            <span>{t('install.ios.share')}</span>
          </li>
          <li>
            <AddGlyph />
            <span>{t('install.ios.add')}</span>
          </li>
        </ol>
      )}

      {route === 'ios-other' && (
        <>
          <button type="button" className="inst-do" onClick={toSafari}>
            {t('install.ios.open')}
          </button>
          {/*
            Shown only once the hand-off has visibly not happened. Offering it up front would
            be explaining the fallback before trying the thing that usually works, which is the
            wall of text this replaces.
          */}
          {handingOff && (
            <div className="inst-fallback">
              <p className="inst-why">{t('install.ios.manual')}</p>
              <button type="button" className="inst-copy" onClick={() => void copy()}>
                {copied ? t('install.copied') : t('install.copy')}
              </button>
              <ol className="inst-steps">
                <li>
                  <ShareGlyph />
                  <span>{t('install.ios.share')}</span>
                </li>
                <li>
                  <AddGlyph />
                  <span>{t('install.ios.add')}</span>
                </li>
              </ol>
            </div>
          )}
        </>
      )}
    </section>
  );
}
